/**
 * `/employees` (PRD §4.3) — one row per human, no year filter anywhere.
 *
 * A person belongs to the company; the project, the entry date and the R&D share belong to the
 * assignment, which is why the Projekte column carries one chip per assignment instead of the
 * page carrying a year selector. What a company admin may touch is decided by the server and
 * arrives per assignment as `canEdit` / `canRemove` (PLAN.md D-22) — the client never guesses.
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { MoreHorizontal, Pencil, Plus, Upload, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BackLink } from "@/components/back-link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AssignmentDialog } from "@/components/employees/assignment-dialog";
import { AddEmployeeDialog, EditEmployeeDialog } from "@/components/employees/employee-dialogs";
import { ImportDialog } from "@/components/employees/import-dialog";
import { EmployeeNameCell, TwoLineTableHead } from "@/components/employees/person-status-badges";
import { describeError } from "@/lib/errors";
import { toastApiError } from "@/lib/rate-limit";
import { useQueryFlag } from "@/lib/query-param";
import { formatDate, formatHours, formatPercent, formatPersonName } from "@/lib/format";
import { useSession } from "@/lib/session";
import {
  filterArchived,
  useChangeRole,
  useCompanies,
  useEmployees,
  useInvitePeople,
  usePersonLifecycle,
  useProjects,
  useUnassign,
} from "@/lib/tenancy";
import {
  isNeverSignedIn,
  type AssignmentSummary,
  type PersonSummary,
  type ProjectSummary,
} from "@/shared/schemas/tenancy";

export function EmployeesPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";

  const [params, setParams] = useSearchParams();
  const companies = useCompanies();

  // PLAN.md D-27: a company admin has exactly one company; Grantonomy picks one, and the choice
  // lives in the URL so the page can be linked to from a project or a company.
  const companyId = isGAdmin
    ? (params.get("company") ?? companies.data?.[0]?.id ?? "")
    : (user?.companyId ?? "");
  const projectId = params.get("project");
  const fromCompany = params.get("from") === "company";

  // Back only from project Live (`?project=`) or company detail (`?from=company`).
  // Sidebar /employees is a sibling of Projects — no always-on "Back to projects".
  const backTarget =
    projectId && companyId
      ? { href: `/projects/${projectId}/live`, label: t("employees.backToProject") }
      : fromCompany && companyId
        ? { href: `/companies/${companyId}`, label: t("employees.backToCompany") }
        : null;

  // Ticket 92.1: show-archived is G-Admin only — company admins always see active people.
  const [showArchivedFlag, setShowArchived] = useQueryFlag("archived");
  const showArchived = isGAdmin && showArchivedFlag;
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  // G-Admin fetches archived too so the toggle can reveal them client-side; CA stays active-only.
  const people = useEmployees(companyId || undefined, isGAdmin ? { archived: true } : undefined);
  const projects = useProjects({ companyId: companyId || undefined });
  const invite = useInvitePeople(companyId);

  const visible = useMemo(
    () => filterPeople(filterArchived(people.data ?? [], showArchived), search),
    [people.data, search, showArchived],
  );
  const uninvited = (people.data ?? []).filter(
    (person) => !person.archived && isNeverSignedIn(person),
  );

  const tableMessage = !companyId
    ? t("employees.chooseCompanyBody")
    : people.isLoading && !people.data
      ? null
      : (people.data ?? []).length === 0
        ? t("employees.empty")
        : visible.length === 0
          ? t("employees.noMatches")
          : null;

  function inviteAll(userIds: string[]) {
    if (userIds.length === 0) return;
    invite.mutate(
      { userIds },
      {
        onSuccess: (data) => {
          const sent = data.results.filter(
            (entry) => entry.status === "queued" || entry.status === "deduped",
          ).length;
          toast.success(t("employees.inviteResult", { sent, skipped: data.results.length - sent }));
        },
        onError: (error) => toastApiError(error),
      },
    );
  }

  return (
    <div className="flex h-[calc(100svh-5rem)] min-h-0 flex-col">
      {backTarget && (
        <BackLink to={backTarget.href} data-testid="employees-back">
          {backTarget.label}
        </BackLink>
      )}

      <div
        className={`flex shrink-0 flex-wrap items-end justify-between gap-4 ${backTarget ? "mt-4" : ""}`}
      >
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            {t("employees.kicker")}
          </div>
          <h1 className="mt-2 text-4xl">{t("employees.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("employees.intro")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={!companyId || (projects.data ?? []).length === 0}
            onClick={() => setImporting(true)}
          >
            <Upload className="size-4" /> {t("employees.import")}
          </Button>
          <Button disabled={!companyId} onClick={() => setAdding(true)}>
            <Plus className="size-4" /> {t("employees.add")}
          </Button>
        </div>
      </div>

      <div className="mt-8 flex shrink-0 flex-wrap items-center gap-4">
        {isGAdmin && (
          <Select
            value={companyId}
            onValueChange={(value) => setParams({ company: value }, { replace: true })}
          >
            <SelectTrigger className="w-64" aria-label={t("employees.chooseCompany")}>
              <SelectValue placeholder={t("employees.chooseCompany")} />
            </SelectTrigger>
            <SelectContent>
              {(companies.data ?? []).map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Input
          className="w-64"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("employees.searchPlaceholder")}
          aria-label={t("employees.search")}
        />

        {isGAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showArchived}
              onCheckedChange={(value) => setShowArchived(value === true)}
            />
            {t("employees.showArchived")}
          </label>
        )}
      </div>

      {uninvited.length > 0 && (
        <div className="mt-6 flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface p-4 text-sm">
          <span>
            {t("employees.neverSignedIn")} · {uninvited.length}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={invite.isPending}
            onClick={() => inviteAll(uninvited.map((person) => person.id))}
          >
            {t("employees.inviteMany", { count: uninvited.length })}
          </Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className="mt-6 max-h-[min(100%,max-content)] overflow-auto rounded-xl border border-border bg-card shadow-soft"
          data-testid="employees-table-scroll"
        >
          {tableMessage === null && people.isLoading && !people.data ? (
            <Skeleton className="m-6 h-32" />
          ) : (
            <Table containerClassName="overflow-visible">
              <EmployeesTableHeader t={t} />
              <TableBody data-testid="employees-table">
                {tableMessage ? (
                  <TableRow data-testid="employees-table-empty-row">
                    <TableCell
                      colSpan={7}
                      className="h-10 text-center text-sm text-muted-foreground"
                    >
                      {tableMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((person) => (
                    <PersonRow
                      key={person.id}
                      person={person}
                      isGAdmin={isGAdmin}
                      projects={projects.data ?? []}
                      onInvite={() => inviteAll([person.id])}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {companyId && adding && (
        <AddEmployeeDialog
          open
          onOpenChange={setAdding}
          companyId={companyId}
          projects={projects.data ?? []}
        />
      )}
      {companyId && importing && (
        <ImportDialog
          open
          onOpenChange={setImporting}
          companyId={companyId}
          projects={projects.data ?? []}
          onImported={() => void people.refetch()}
        />
      )}
    </div>
  );
}

function PersonRow({
  person,
  isGAdmin,
  projects,
  onInvite,
}: {
  person: PersonSummary;
  isGAdmin: boolean;
  projects: ProjectSummary[];
  onInvite: () => void;
}) {
  const { t } = useTranslation();
  const changeRole = useChangeRole();
  const lifecycle = usePersonLifecycle();
  const unassign = useUnassign();

  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [editAssignment, setEditAssignment] = useState<AssignmentSummary | null>(null);
  const [removing, setRemoving] = useState<AssignmentSummary | null>(null);
  const [archiving, setArchiving] = useState(false);

  const fullName = formatPersonName(person);

  function setRole(role: "company_admin" | "employee") {
    changeRole.mutate(
      { id: person.id, role },
      {
        onSuccess: () => toast.success(t("employees.roleChanged")),
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function setArchived(action: "archive" | "restore") {
    lifecycle.mutate(
      { id: person.id, action },
      {
        onSuccess: () => {
          toast.success(t(action === "archive" ? "employees.archived" : "employees.restored"));
          setArchiving(false);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  function removeAssignment(assignment: AssignmentSummary) {
    unassign.mutate(
      { id: assignment.id },
      {
        onSuccess: (result) => {
          toast.success(
            t(
              result.removed === "archived"
                ? "employees.assignmentArchived"
                : "employees.assignmentRemoved",
            ),
          );
          setRemoving(null);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  return (
    <TableRow className={person.archived ? "opacity-60" : undefined}>
      <TableCell>
        <EmployeeNameCell person={person} showArchived />
      </TableCell>
      <TableCell className="text-muted-foreground" data-testid={`employee-position-${person.id}`}>
        {person.position ?? "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">{person.email}</TableCell>
      <TableCell className="text-right">{formatHours(person.weeklyHours)}</TableCell>
      <TableCell className="text-right">{person.workdaysPerWeek ?? "—"}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          {person.assignments.length === 0 && (
            <span className="text-sm text-muted-foreground">{t("employees.noAssignments")}</span>
          )}
          {person.assignments.map((assignment) => (
            <AssignmentChip
              key={assignment.id}
              assignment={assignment}
              onEdit={() => setEditAssignment(assignment)}
              onRemove={() => setRemoving(assignment)}
            />
          ))}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t("employees.colActions")}>
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setAssigning(true)}>
              {t("employees.assign")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onInvite}>
              {person.invitedAt ? t("employees.inviteAgain") : t("employees.invite")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                setRole(person.role === "company_admin" ? "employee" : "company_admin")
              }
            >
              {person.role === "company_admin" ? t("employees.demote") : t("employees.promote")}
            </DropdownMenuItem>
            {/* Matrix #5/#6: master data and archiving are Grantonomy's, so a CA sees neither. */}
            {isGAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  {t("employees.edit")}
                </DropdownMenuItem>
                {person.archived ? (
                  <DropdownMenuItem onSelect={() => setArchived("restore")}>
                    {t("employees.restore")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => setArchiving(true)}>
                    {t("employees.archive")}
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {editing && <EditEmployeeDialog person={person} onOpenChange={setEditing} />}
        {assigning && (
          <AssignmentDialog
            person={person}
            assignment={null}
            projects={projects}
            onOpenChange={setAssigning}
          />
        )}
        {editAssignment && (
          <AssignmentDialog
            person={person}
            assignment={editAssignment}
            projects={projects}
            onOpenChange={() => setEditAssignment(null)}
          />
        )}
        <ConfirmDialog
          open={archiving}
          onOpenChange={setArchiving}
          title={t("employees.archiveTitle")}
          body={t("employees.archiveBody", { name: fullName })}
          confirmLabel={t("employees.archive")}
          busy={lifecycle.isPending}
          onConfirm={() => setArchived("archive")}
        />
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(open) => !open && setRemoving(null)}
          title={t("employees.removeAssignmentTitle")}
          body={t("employees.removeAssignmentBody", {
            name: fullName,
            project: removing?.projectName ?? "",
          })}
          confirmLabel={t("employees.removeAssignment")}
          destructive
          busy={unassign.isPending}
          onConfirm={() => removing && removeAssignment(removing)}
        />
      </TableCell>
    </TableRow>
  );
}

function AssignmentChip({
  assignment,
  onEdit,
  onRemove,
}: {
  assignment: AssignmentSummary;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const locked = t("employees.lockedTooltip");

  return (
    <span
      className={`inline-flex max-w-full items-center gap-2 whitespace-nowrap rounded-md border border-border px-2 py-1 text-xs ${
        assignment.archived ? "opacity-60" : ""
      }`}
    >
      <span
        className="max-w-[10rem] truncate font-medium"
        title={assignment.projectName}
        data-testid={`assignment-project-name-${assignment.id}`}
      >
        {assignment.projectName}
      </span>
      <span className="text-muted-foreground">
        {formatDate(assignment.entryDate)} · {formatPercent(assignment.rdShare)}
        {assignment.leaveDate && !assignment.archived
          ? ` · ${formatDate(assignment.leaveDate)}`
          : ""}
      </span>
      {assignment.archived && <Badge variant="secondary">{t("employees.archivedBadge")}</Badge>}

      {(assignment.canEdit || assignment.canEditLeaveDate) && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={onEdit}
          aria-label={t("employees.editAssignment")}
        >
          <Pencil className="size-3" />
        </button>
      )}
      <button
        type="button"
        className="text-muted-foreground hover:text-destructive"
        title={assignment.canRemove ? undefined : locked}
        onClick={() => {
          if (assignment.canRemove) {
            onRemove();
            return;
          }
          toast.error(t("errors.ASSIGNMENT_HAS_ENTRIES"));
        }}
        aria-label={t("employees.removeAssignment")}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

function EmployeesTableHeader({ t }: { t: TFunction }) {
  return (
    <TableHeader className="sticky top-0 z-10 bg-card [&_th]:bg-card">
      <TableRow>
        <TableHead>{t("employees.colName")}</TableHead>
        <TableHead>{t("employees.colPosition")}</TableHead>
        <TableHead>{t("employees.colEmail")}</TableHead>
        <TwoLineTableHead
          className="w-24 text-right"
          testId="employees-col-hours"
          line1={t("employees.colHoursLine1")}
          line2={t("employees.colHoursLine2")}
        />
        <TwoLineTableHead
          className="w-24 text-right"
          testId="employees-col-days"
          line1={t("employees.colDaysLine1")}
          line2={t("employees.colDaysLine2")}
        />
        <TableHead>{t("employees.colProjects")}</TableHead>
        <TableHead className="w-16 text-right">{t("employees.colActions")}</TableHead>
      </TableRow>
    </TableHeader>
  );
}

/** Search and sort stay in the browser: one company's staff is a small, already-loaded list. */
function filterPeople(people: PersonSummary[], search: string): PersonSummary[] {
  const needle = search.trim().toLowerCase();
  const matched = needle
    ? people.filter((person) =>
        `${formatPersonName(person)} ${person.email} ${person.position ?? ""}`
          .toLowerCase()
          .includes(needle),
      )
    : people;
  return [...matched].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "de"),
  );
}
